import React from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import OpenNewTab from 'components/icons/OpenNewTab';

const StyledButton = styled.button`
  border-radius: 0.625rem;
  ${({ showNewSchemaColor, textColor, outlined }) =>
    showNewSchemaColor && outlined && `border-color: ${textColor};`}
  ${({ showNewSchemaColor, textColor, outlined }) =>
    showNewSchemaColor && outlined && `color: ${textColor};`}
  
  ${({ showNewSchemaColor, color, filled }) =>
    showNewSchemaColor && filled && `background-color: ${color};`}
  ${({ showNewSchemaColor, textColor, filled }) =>
    showNewSchemaColor && filled && `color: ${textColor};`}
`;

const Button = ({
  children,
  className,
  variant = 'filled',
  color = 'action-blue',
  textColor = 'action-blue',
  onClick,
  href,
  target,
  fullWidthMobile = false,
  downloadable = false,
  downloadLabel = '',
  disabled,
  disableHover = false,
  buttonDescription = '',
  linkWrapperDescription = '',
  height = false,
  linkRole = 'link',
  buttonRef,
  linkRef,
  showNewSchemaColor = false,
  ...rest
}) => {
  const isLink = Boolean(href);
  const description = isLink
    ? linkWrapperDescription || buttonDescription
    : buttonDescription;
  const ariaLabel =
    description && target === '_blank' && href
      ? `${description}. Opens in new tab`
      : description || undefined;

  return (
    <StyledButton
      as={isLink ? 'a' : 'button'}
      href={isLink ? href : undefined}
      target={isLink ? target || '_self' : undefined}
      download={isLink && downloadable ? downloadLabel || '' : undefined}
      role={isLink ? linkRole : undefined}
      tabIndex={isLink ? undefined : '0'}
      showNewSchemaColor={showNewSchemaColor}
      color={color}
      textColor={textColor}
      outlined={variant === 'outlined'}
      filled={variant === 'filled'}
      className={cx(
        'inline-block uppercase font-trade-gothic-20 py-4 px-8 tracking-wide w-full md:w-auto content-center',
        {
          'w-full': fullWidthMobile,
          'h-60-mattamy': !height,
          'text-white bg-action-blue hover:bg-mattamy-blue':
            variant === 'filled' && color === 'action-blue',
          'text-white bg-mattamy-blue':
            variant === 'filled' && color === 'mattamy-blue',
          'text-mattamy-blue bg-white border-1-5 border-white':
            variant === 'filled' &&
            color === 'white' &&
            textColor === 'mattamy-blue',
          'text-action-blue bg-white':
            variant === 'filled' &&
            color === 'white' &&
            textColor === 'action-blue',
          'text-action-blue border-1-5 bg-transparent border-action-blue hover:bg-action-blue hover:text-white':
            variant === 'outlined' &&
            color === 'action-blue' &&
            textColor === 'action-blue',
          'text-mattamy-blue border-1-5 bg-transparent border-action-blue':
            variant === 'outlined' &&
            color === 'mattamy-blue' &&
            textColor === 'mattamy-blue',
          'text-white border-1-5 bg-transparent border-white hover:bg-white hover:text-mattamy-blue':
            variant === 'outlined' && color === 'white',
          'hover:text-mattamy-blue hover:bg-white':
            variant === 'outlined' &&
            color === 'white' &&
            !disabled &&
            disableHover,
          'text-mattamy-blue border-1-5 bg-transparent border-mattamy-blue hover:text-white hover:bg-mattamy-blue':
            variant === 'outlined' && color === 'mattamy-blue',
          'cursor-pointer': !disabled,
          'cursor-default': disabled,
          'border-1-5 bg-transparent':
            showNewSchemaColor && variant === 'outlined',
        },
        className,
      )}
      disabled={!isLink ? disabled : undefined}
      onClick={(e) => {
        typeof onClick === 'function' && onClick(e);
      }}
      aria-label={ariaLabel}
      ref={isLink ? linkRef : buttonRef}
      {...rest}
    >
      <div
        style={{ minWidth: '110px', width: '100%' }}
        className='mt-1 flex justify-center items-center w-full'
      >
        {children}
        {target === '_blank' && href && (
          <OpenNewTab className='mb-1 ml-1 w-5' aria-hidden='true' />
        )}
      </div>
    </StyledButton>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['outlined', 'filled']),
  color: PropTypes.oneOf(['action-blue', 'mattamy-blue']),
  textColor: PropTypes.oneOf(['action-blue', 'mattamy-blue', 'white']),
  onClick: PropTypes.func,
  href: PropTypes.string,
  target: PropTypes.string,
  fullWidthMobile: PropTypes.bool,
};

export default Button;
